import { useCallback, useEffect, useState, useSyncExternalStore } from "react";

import {
  appVersionUpdateActions,
  appVersionUpdateStore,
  UnsavedWorkCollection,
} from "./app-version-update.store";
import {
  isDeploymentOptInEnabled,
  setDeploymentOptInEnabled,
} from "./deployment-opt-in-preference";

type UseAppVersionUpdate = {
  // True while a new version is waiting in opt-in mode — drives the footer button.
  isUpdateAvailable: boolean;
  // Non-null while the confirmation modal should be shown (footer click, or a
  // morning return that found unsaved work).
  pendingConfirm: UnsavedWorkCollection | null;
  // Footer button: collect unsaved work then open the confirmation modal.
  requestUpdate: () => Promise<void>;
  // Confirm from the modal → apply the coordinated update.
  confirmUpdate: () => void;
  // Dismiss the modal without applying.
  dismissUpdate: () => void;
  isOptInEnabled: boolean;
  setOptInEnabled: (enabled: boolean) => void;
};

export const useAppVersionUpdate = (): UseAppVersionUpdate => {
  const { isUpdateAvailable, pendingConfirm } = useSyncExternalStore(
    appVersionUpdateStore.subscribe,
    appVersionUpdateStore.getSnapshot
  );

  // The preference is plain localStorage (read synchronously elsewhere); mirror it
  // in local state so a toggle re-renders immediately.
  const [isOptInEnabled, setIsOptInEnabled] = useState(isDeploymentOptInEnabled);

  // localStorage is shared across tabs: when another tab enables the opt-in, the
  // `storage` event (which only fires in the *other* tabs) refreshes this mirror
  // so the footer toggle never shows a stale value.
  useEffect((): (() => void) => {
    const sync = (): void => setIsOptInEnabled(isDeploymentOptInEnabled());
    window.addEventListener("storage", sync);
    return (): void => window.removeEventListener("storage", sync);
  }, []);

  const setOptInEnabled = useCallback((enabled: boolean): void => {
    setDeploymentOptInEnabled(enabled);
    setIsOptInEnabled(enabled);
  }, []);

  return {
    isUpdateAvailable,
    pendingConfirm,
    requestUpdate: appVersionUpdateActions.requestUpdate,
    confirmUpdate: appVersionUpdateActions.confirmUpdate,
    dismissUpdate: appVersionUpdateActions.dismissUpdate,
    isOptInEnabled,
    setOptInEnabled,
  };
};
